import React, { createContext, useContext, useReducer } from "react";
import type {
	RatingFormState,
	ReviewFrequency,
	ReviewLevel,
	SubCriterionRating,
} from "newui/features/MySuppliers/types/supplier-performance.types";

// Action types
type RatingWorkflowAction =
	| { type: "SET_SUPPLIER"; payload: { supplierId: number; supplierName: string } }
	| { type: "SET_PROJECT"; payload: { projectId: number | null; rfxId: number | null; rfxInviteId: number | null; reviewLevel: ReviewLevel } }
	| { type: "SET_FREQUENCY"; payload: { frequency: ReviewFrequency } }
	| { type: "SET_CATEGORIES"; payload: { categoryIds: number[] } }
	| { type: "SET_SUB_CRITERION_RATING"; payload: { categoryId: number; subCriterionId: number; score: number; comment: string | null } }
	| { type: "SET_DRAFT_ID"; payload: { draftId: number } }
	| { type: "RESET" };

const initialState: RatingFormState = {
	supplierId: null,
	supplierName: "",
	projectId: null,
	rfxId: null,
	rfxInviteId: null,
	reviewLevel: "rfx",
	frequency: "one_off",
	selectedCategoryIds: [],
	ratings: {},
	draftId: null,
};

const ratingWorkflowReducer = (
	state: RatingFormState,
	action: RatingWorkflowAction
): RatingFormState => {
	switch (action.type) {
		case "SET_SUPPLIER":
			return {
				...state,
				supplierId: action.payload.supplierId,
				supplierName: action.payload.supplierName,
			};
		case "SET_PROJECT":
			return {
				...state,
				projectId: action.payload.projectId,
				rfxId: action.payload.rfxId,
				rfxInviteId: action.payload.rfxInviteId,
				reviewLevel: action.payload.reviewLevel,
			};
		case "SET_FREQUENCY":
			return {
				...state,
				frequency: action.payload.frequency,
			};
		case "SET_CATEGORIES":
			return {
				...state,
				selectedCategoryIds: action.payload.categoryIds,
			};
		case "SET_SUB_CRITERION_RATING": {
			const { categoryId, subCriterionId, score, comment } =
				action.payload;
			const categoryRatings = state.ratings[categoryId] ?? [];
			const existingIndex = categoryRatings.findIndex(
				(r) => r.subCriterionId === subCriterionId
			);

			const updatedRating: SubCriterionRating = {
				subCriterionId,
				score,
				comment,
			};

			let updatedCategoryRatings: SubCriterionRating[];
			if (existingIndex >= 0) {
				updatedCategoryRatings = [...categoryRatings];
				updatedCategoryRatings[existingIndex] = updatedRating;
			} else {
				updatedCategoryRatings = [
					...categoryRatings,
					updatedRating,
				];
			}

			return {
				...state,
				ratings: {
					...state.ratings,
					[categoryId]: updatedCategoryRatings,
				},
			};
		}
		case "SET_DRAFT_ID":
			return {
				...state,
				draftId: action.payload.draftId,
			};
		case "RESET":
			return initialState;
		default:
			return state;
	}
};

// Context
interface RatingWorkflowContextValue {
	state: RatingFormState;
	dispatch: React.Dispatch<RatingWorkflowAction>;
}

const RatingWorkflowContext = createContext<RatingWorkflowContextValue | null>(
	null
);

interface RatingWorkflowProviderProps {
	children: React.ReactNode;
	initialSupplier?: { supplierId: number; supplierName: string } | null;
}

export const RatingWorkflowProvider: React.FC<RatingWorkflowProviderProps> = ({
	children,
	initialSupplier,
}) => {
	const initial: RatingFormState = initialSupplier
		? {
				...initialState,
				supplierId: initialSupplier.supplierId,
				supplierName: initialSupplier.supplierName,
			}
		: initialState;

	const [state, dispatch] = useReducer(ratingWorkflowReducer, initial);

	return (
		<RatingWorkflowContext.Provider value={{ state, dispatch }}>
			{children}
		</RatingWorkflowContext.Provider>
	);
};

export const useRatingWorkflow = (): RatingWorkflowContextValue => {
	const context = useContext(RatingWorkflowContext);
	if (!context) {
		throw new Error(
			"useRatingWorkflow must be used within RatingWorkflowProvider"
		);
	}
	return context;
};

export type { RatingWorkflowAction };
